import { Route } from 'react-router-dom';
import { SentryRoutes as Routes } from '../index';
import MainLayout from '../layouts/MainLayout';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  authStatusEnum,
  BrandDetailsLocation,
  setCompanyFavicon,
  setCompanyLogo,
  setCompanyName,
  setCompanyWebsite,
  setCompanyWhiteLogo,
  setCustomerCare,
  setFullPageThreeDotsLoading,
  setPrimaryColor,
  setPrivacyPolicyUrl,
  setTermsOfServiceUrl,
} from '../store/auth/authSlice';
import CustomSnackbar from '../components/CustomSnackbar';
import { setToastState } from '../store/toast/toastSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import LoadingScreen from '../components/LoadingScreen';
import TimerPage from '../pages/TimerPage';
import AdaptiveLayout from '../layouts/AdaptiveAssessmentLayout';
import GenericCameraComponent from '../components/GenericCameraComponent';
import RedirectPage from '../pages/AttemptTryQuestionPage/components/redirect/RedirectPage';
import TryQuestionLayout from '../layouts/TryQuestionLayout';
import { fetchBrandDetails } from '../services/api/auth';
import { defaultWhiteLabelProps, setFavicons } from '../utils/helper';

const SignInPage = lazy(() => import('../pages/SignInPage'));
const AttemptAssessmentPage = lazy(
  () => import('../pages/AttemptAssessmentPage')
);
const InstructionPage = lazy(() => import('../pages/InstructionPage'));
const FinishPage = lazy(() => import('../pages/FinishPage'));
const ExpiryPage = lazy(() => import('../pages/ExpiryPage'));
const ErrorPage = lazy(() => import('../pages/ErrorPage'));
const ThankyouPage = lazy(() => import('../pages/ThankyouPage'));
const HunchScoreVideoPage = lazy(() => import('../pages/HunchScoreVideoPage'));

const AdaptiveAssessmentPage = lazy(
  () => import('../pages/AdaptiveAssessmentPage')
);
const AttempTryQuestionPage = lazy(
  () => import('../pages/AttemptTryQuestionPage/index')
);
const AttemptAIQuestionPage = lazy(
  () => import('../pages/AIBotPages/AttemptAIQuestionPage')
);

const AppRoutes = () => {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);
  const toastState = useAppSelector((state) => state.toast);
  const [elementsUpdated, setElementsUpdated] = useState<number>(0);

  const is_adaptive = useAppSelector((state) => state.assessment.is_adaptive);
  const assessment = useAppSelector((state) => state.assessment);
  const showInstruction = authState.status === authStatusEnum.LANDED;
  const showLogin = authState.status === authStatusEnum.CONTINUED;
  const showError = authState.status === authStatusEnum.REJECTED;
  const showTimer = authState.status === authStatusEnum.TIMER;
  const showAttempt = authState.status === authStatusEnum.AUTHENITICATED;
  const showFinish = authState.status === authStatusEnum.ENDED;
  const showExpiry = authState.status === authStatusEnum.EXPIRY;
  const showThankyou = authState.status === authStatusEnum.THANKYOU;

  useEffect(() => {
    if (authState.darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [authState.darkMode]);

  const closeSnackbar = () =>
    dispatch(
      setToastState({
        ...toastState,
        open: false,
        allowClose: true,
        autoHideDuration: 2000,
      })
    );

  const hideHubSpot = useCallback(() => {
    const hubSpotEl = document.getElementById(
      'hubspot-messages-iframe-container'
    );
    if (hubSpotEl) {
      hubSpotEl.style.display = 'none !important';
    }
    if (window?.HubSpotConversations?.widget?.remove) {
      window?.HubSpotConversations?.widget.remove();
    }
  }, []);
  // dummy cmmit

  const loadHubSpot = () => {
    const hubSpotEl = document.getElementById(
      'hubspot-messages-iframe-container'
    );
    if (hubSpotEl) {
      hubSpotEl.style.display = 'initial !important';
    }
    try {
      if (window?.HubSpotConversations?.widget?.status) {
        const status = window?.HubSpotConversations?.widget?.status();
        const { loaded, pending } = status;
        if (loaded || pending) {
          return;
        }
        if (window.HubSpotConversations.widget.load)
          window.HubSpotConversations.widget!.load({
            inlineEmbedSelector: '#hubspot-overlay-container',
          });
      } else if (window?.HubSpotConversations?.widget?.load) {
        window.HubSpotConversations.widget!.load({
          inlineEmbedSelector: '#hubspot-overlay-container',
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              if (node.id === 'hubspot-messages-iframe-container') {
                setElementsUpdated((prev) => prev + 1);
              }
            }
          });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: false,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (showAttempt) {
      hideHubSpot();
    } else {
      loadHubSpot();
    }
  }, [assessment.is_bot, showAttempt, showFinish, elementsUpdated]);

  useEffect(() => {
    const getBrandDetails = async () => {
      try {
        const companyName = window.location.hostname.split('.')[1];
        dispatch(
          setFullPageThreeDotsLoading(authState.fullPageThreeDotsLoading + 1)
        );
        const res = await fetchBrandDetails(
          companyName,
          BrandDetailsLocation.AUTHENTICATION
        );

        if (!res.success) {
          return;
        }

        if (res?.data?.company_name === 'HireHunch') {
          dispatch(setPrimaryColor(defaultWhiteLabelProps.primaryColor));
          dispatch(setCompanyName(defaultWhiteLabelProps.companyName));
          dispatch(setCompanyLogo(defaultWhiteLabelProps.companyLogo));
          return;
        }

        if (
          (res?.data?.company_favicon ?? '').length > 0 &&
          res?.data?.company_name !== 'HireHunch'
        ) {
          dispatch(setCompanyFavicon(res?.data?.company_favicon));
          setFavicons(res?.data?.company_favicon);
        }

        if (res?.data?.primary_color || res?.data?.secondary_color) {
          let pc =
            res?.data?.primary_color ?? defaultWhiteLabelProps.primaryColor;

          dispatch(setPrimaryColor(pc));
          const event = new CustomEvent('updateTheme', {
            detail: {
              primaryColor: pc,
            },
          });
          document.dispatchEvent(event);
        }

        if ((res?.data?.brand_logo_url ?? '').length > 0) {
          dispatch(setCompanyLogo(res?.data?.brand_logo_url));
        } else {
          dispatch(setCompanyLogo(defaultWhiteLabelProps.companyLogo));
        }

        if ((res?.data?.brand_white_logo_url ?? '').length > 0) {
          dispatch(setCompanyWhiteLogo(res?.data?.brand_white_logo_url));
        }

        if ((res?.data?.company_name ?? '').length > 0) {
          const title =
            res?.data?.company_name ?? defaultWhiteLabelProps.companyName;
          dispatch(setCompanyName(title));
          document.title = `Assessment | ${title} App`;
        }
        if ((res?.data?.customer_care_email ?? '').length > 0) {
          dispatch(setCustomerCare(res?.data?.customer_care_email));
        }

        if ((res?.data?.brand_website ?? '').length > 0) {
          dispatch(setCompanyWebsite(res?.data?.brand_website));
        }

        if ((res?.data?.terms_of_service_url ?? '').length > 0) {
          dispatch(setTermsOfServiceUrl(res?.data?.terms_of_service_url));
        }

        if ((res?.data?.privacy_policy_url ?? '').length > 0) {
          dispatch(setPrivacyPolicyUrl(res?.data?.privacy_policy_url));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setTimeout(() => {
          setFullPageThreeDotsLoading(authState.fullPageThreeDotsLoading - 1);
        }, 400);
      }
    };
    const companyName = window.location.hostname.split('.')[1];
    if (companyName && companyName.toLowerCase() !== 'hirehunch') {
      getBrandDetails();
    }
  }, []);

  return (
    <>
      <CustomSnackbar
        open={toastState.open}
        onClose={closeSnackbar}
        type={toastState.type}
        message={toastState.message}
        autoHideDuration={toastState.autoHideDuration}
        allowClose={toastState.allowClose}
        messageType={toastState.messageType}
      />
      {!assessment.is_bot && <GenericCameraComponent />}
      <Routes>
        {showTimer && (
          <Route
            path="/open-interview/:visitorId/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <TimerPage />
              </Suspense>
            }
          />
        )}
        {assessment.is_bot && showAttempt && (
          <Route
            path="/open-interview/:visitorId/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <AttemptAIQuestionPage />
              </Suspense>
            }
          />
        )}
        {showFinish && (
          <Route
            path="/open-interview/:visitorId/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <FinishPage />
              </Suspense>
            }
          />
        )}
        {showError && (
          <Route
            path="/open-interview/:visitorId/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                {/* <ErrorPage /> */}
              </Suspense>
            }
          />
        )}
        {showThankyou && (
          <Route
            path="/open-interview/:visitorId/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ThankyouPage />
              </Suspense>
            }
          />
        )}
        {showExpiry && (
          <Route
            path="open-interview/:visitorId/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ExpiryPage />
              </Suspense>
            }
          />
        )}
        <Route
          path="/open-interview/:visitorId"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <HunchScoreVideoPage />
            </Suspense>
          }
        />

        <Route
          path="/open-interview/:visitorId/:accessKey"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <HunchScoreVideoPage />
            </Suspense>
          }
        />

        {/* Routes for assessments */}
        <Route
          path="/completed"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <RedirectPage />
            </Suspense>
          }
        />
        <Route
          path="/:interviewerId/:questionId"
          element={<TryQuestionLayout />}
        >
          <Route
            path=""
            element={
              <Suspense fallback={<LoadingScreen />}>
                <AttempTryQuestionPage />
              </Suspense>
            }
          />
        </Route>
        {showInstruction && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <InstructionPage />
              </Suspense>
            }
          />
        )}
        {showLogin && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <SignInPage />
              </Suspense>
            }
          />
        )}
        {showTimer && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <TimerPage />
              </Suspense>
            }
          />
        )}
        {!assessment.is_bot &&
          showAttempt &&
          (is_adaptive ? (
            <Route element={<AdaptiveLayout />}>
              <Route
                path="/:accessKey"
                element={
                  <Suspense fallback={<LoadingScreen />}>
                    <AdaptiveAssessmentPage />
                  </Suspense>
                }
              />
            </Route>
          ) : (
            <Route element={<MainLayout />}>
              <Route
                path="/:accessKey"
                element={
                  <Suspense fallback={<LoadingScreen />}>
                    <AttemptAssessmentPage />
                  </Suspense>
                }
              />
            </Route>
          ))}

        {assessment.is_bot && showAttempt && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <AttemptAIQuestionPage />
              </Suspense>
            }
          />
        )}
        {showFinish && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <FinishPage />
              </Suspense>
            }
          />
        )}

        {showExpiry && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ExpiryPage />
              </Suspense>
            }
          />
        )}

        {showError && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                {/* <ErrorPage /> */}
              </Suspense>
            }
          />
        )}
        {showThankyou && (
          <Route
            path="/:accessKey"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ThankyouPage />
              </Suspense>
            }
          />
        )}
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorPage />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
};

export default AppRoutes;
